import { format, formatDistanceToNow, isToday, isYesterday, isTomorrow, parseISO } from 'date-fns';

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

// Format number with commas
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// Format date
export const formatDate = (date, formatStr = 'MMM d, yyyy') => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr);
};

// Format relative date
export const formatRelativeDate = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : date;

  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  if (isTomorrow(d)) return 'Tomorrow';

  return formatDistanceToNow(d, { addSuffix: true });
};

// Get Instagram profile URL
export const getInstagramProfileUrl = (handle) => {
  if (!handle) return '';
  const cleanHandle = handle.replace('@', '');
  return `https://instagram.com/${cleanHandle}`;
};

// Get Instagram DM URL
export const getInstagramDmUrl = (handle) => {
  if (!handle) return '';
  const cleanHandle = handle.replace('@', '');
  return `https://ig.me/m/${cleanHandle}`;
};

// Format Instagram handle
export const formatInstagramHandle = (handle) => {
  if (!handle) return '';
  return handle.startsWith('@') ? handle : `@${handle}`;
};

// Calculate days in stage
export const getDaysInStage = (stageUpdatedAt) => {
  if (!stageUpdatedAt) return 0;
  const date = typeof stageUpdatedAt === 'string' ? parseISO(stageUpdatedAt) : stageUpdatedAt;
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Stage display names
export const stageDisplayNames = {
  new: 'New',
  warming: 'Warming Up',
  dm_sent: 'DM Sent',
  replied: 'Replied',
  call_scheduled: 'Call Scheduled',
  call_completed: 'Call Completed',
  proposal_sent: 'Proposal Sent',
  negotiating: 'Negotiating',
  won: 'Won',
  lost: 'Lost',
  not_interested: 'Not Interested',
};

// Get stage display name
export const getStageDisplayName = (stage) => {
  return stageDisplayNames[stage] || stage;
};

// Activity type display names
export const activityTypeNames = {
  like: 'Liked Post',
  comment: 'Commented',
  story_reply: 'Story Reply',
  dm_sent: 'DM Sent',
  dm_received: 'DM Received',
  call_scheduled: 'Call Scheduled',
  call_completed: 'Call Completed',
  proposal_sent: 'Proposal Sent',
  follow_up: 'Follow Up',
  note: 'Note Added',
  stage_change: 'Stage Changed',
  other: 'Other',
};

// Get activity type display name
export const getActivityTypeName = (type) => {
  return activityTypeNames[type] || type;
};

// Activity type icons
export const activityTypeIcons = {
  like: '❤️',
  comment: '💬',
  story_reply: '📖',
  dm_sent: '📤',
  dm_received: '📥',
  call_scheduled: '📅',
  call_completed: '📞',
  proposal_sent: '📄',
  follow_up: '🔄',
  note: '📝',
  stage_change: '🔀',
  other: '✨',
};

// Get activity icon
export const getActivityIcon = (type) => {
  return activityTypeIcons[type] || '✨';
};

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Generate initials from name
export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Parse CSV data
export const parseCSV = (text) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
    if (values.length === 0) continue;

    const row = {};
    headers.forEach((header, index) => {
      let value = values[index] || '';
      value = value.replace(/^["']|["']$/g, '').trim();
      row[header] = value;
    });
    data.push(row);
  }

  return data;
};

// Convert data to CSV
export const toCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  const headers = columns.map(c => c.label || c.field);
  const fields = columns.map(c => c.field);

  const rows = data.map(row => {
    return fields.map(field => {
      let value = row[field];
      if (value === null || value === undefined) value = '';
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

// Download file
export const downloadFile = (content, filename, type = 'text/csv') => {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Validate email
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validate URL
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Copy to clipboard
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};
